import React from 'react';
import { connect } from 'react-redux';
import { activityAddToCartMapper } from 'core/type/addToCartMapper';
import { AddToCart } from 'component/common/AddToCart/AddToCart';
import { getOverlayType } from 'component/page/detail/detailUtils';
import { Button } from '@drone-js/core';
import { OfferNotificationType } from 'component/page/detail/store/availabilityIndex.types';
import { mapDispatchToProps, mapStateToProps, Props } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardBookButtonSection/AddToCartContainer.selectors';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesDummyAddToCartButtonClickEventActivitiesFieldsV1,
    ActivitiesDummyAddToCartButtonSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesDummyAddToCartButtonClickEventActivitiesManifestV1,
    ActivitiesDummyAddToCartButtonSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { scrollToTargetAdjusted } from 'core-framework/util/Dom';
import { CARD_EXPAND_TIME_OFFSET, TAB_HEADER_OFFSET } from 'component/common/DetailInViewWrapper/navigateToHashWithTabBarOffset';
import { selectIsRebrandCartToSaved } from 'core/utils/cart';

// eslint-disable-next-line @agoda/agoda-a11y/missing-keyboard-tests -- @agoda/agoda-a11y/missing-keyboard-tests[2258d667-2861-4f0f-aebd-e2c74d866a62]
const AddToCartContainer: React.FC<Props> = (props) => {
    const {
        cms,
        behavior,
        isWebView,
        selectedOffer,
        selectedOfferId,
        offerTitle,
        isShowDateChipNotification,
        isShowOfferNotification,
        setOfferNotification,
        finalOfferOption,
        travelDate,
        paxInfo,
        isLoggedIn,
    } = props;
    const offer = selectedOffer.offer;
    const isRebrandCartToSavedEnabled = selectIsRebrandCartToSaved(behavior, isWebView);
    const isShowNotification = isShowDateChipNotification || isShowOfferNotification;
    const shouldHideATCButtonForWebview = !isLoggedIn && isWebView && isRebrandCartToSavedEnabled;
    const isShowAddToCart = props.isAddToCartEnabled && !!selectedOffer && !!offer && !shouldHideATCButtonForWebview;
    const isShowDummyAddToCart = isShowAddToCart && isShowNotification;
    const navigationHash = `#available-option-card-${selectedOffer.offerIndex}`;
    const activityId = offer?.activityId;

    React.useEffect(() => {
        if (isShowDummyAddToCart) {
            trackElementEvent<ActivitiesDummyAddToCartButtonSeenEventActivitiesFieldsV1>(
                ActivitiesDummyAddToCartButtonSeenEventActivitiesManifestV1.ids,
                {},
                { offer_id: selectedOfferId, activity_id: activityId },
            );
        }
    }, [isShowDummyAddToCart, selectedOfferId, activityId]);

    if (!isShowAddToCart) {
        return null;
    }

    if (!finalOfferOption && !isShowNotification) {
        return null;
    }

    const onDummyAddToCartClick = () => {
        trackElementEvent<ActivitiesDummyAddToCartButtonClickEventActivitiesFieldsV1>(
            ActivitiesDummyAddToCartButtonClickEventActivitiesManifestV1.ids,
            {},
            { offer_id: selectedOfferId, activity_id: activityId },
        );
        setOfferNotification({
            [OfferNotificationType.DateChip]: isShowDateChipNotification,
            [OfferNotificationType.OptionSpec]: isShowOfferNotification,
        });

        if (props.expandFirstOfferOnDetailsLanding && isShowNotification) {
            setTimeout(() => {
                scrollToTargetAdjusted(navigationHash, TAB_HEADER_OFFSET);
            }, CARD_EXPAND_TIME_OFFSET);
        }
    };

    const activityModel = {
        travelDate,
        paxInfo,
        activityId: offer.activityId?.toString(),
        activityToken: finalOfferOption?.offerOptionToken || '',
    };
    const productItem = activityAddToCartMapper(activityModel);
    const addToCartLabel = isRebrandCartToSavedEnabled ? cms[350833] : cms[152129];

    return isShowDummyAddToCart ? (
        <Button
            onClick={onDummyAddToCartClick}
            data-element-name="activities-dummy-add-to-cart-button"
            data-testid="activities-dummy-add-to-cart-button"
            hierarchy="secondary"
            width={props.buttonWidth ?? 'parent-fit'}
            variant="label"
            state={props.disabled ? 'disabled' : undefined}
            label={addToCartLabel}
            aria-label={addToCartLabel}
        />
    ) : (
        <AddToCart
            productItem={productItem}
            disabled={props.disabled}
            ariaLabel={`${addToCartLabel} ${offerTitle}`}
            overrideShowOverlayType={getOverlayType(behavior?.common?.isCartInterstitialEnabled)}
        />
    );
};
AddToCartContainer.displayName = 'AddToCartContainer';

export default connect(mapStateToProps, mapDispatchToProps)(AddToCartContainer);
